import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authAPI, setTenantToken, setTenantTokenRefresher, tenantAPI } from '../api/client';
import { apiError, goTo } from '../lib/host';
import { useAuth } from './AuthContext';

const TenantContext = createContext(null);

// The company's own login page (this address), coming back to this page afterwards (a path: always this address).
const toLogin = () => goTo(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);

/**
 * Customer approval (the server's {code, message}): a company that has paid and waits for TeleCalX, or was not approved,
 * can't be opened. Sign out and show why on the login page.
 */
function leaveIfNotApproved(err) {
  const code = err.response?.status === 403 && err.response.data?.detail?.code;
  if (code !== 'pending_approval' && code !== 'account_rejected') return false;
  authAPI.logout().catch(() => {}).finally(() => goTo(`/login?notice=${code}`));
  return true;
}

export const TenantProvider = ({ slug, children }) => {
  const { session, signedOut, loading: authLoading } = useAuth();
  // Only a different user (or logout) should re-open the organization, not every silent session refresh.
  const userId = session?.user?.id ?? null;
  const [state, setState] = useState({ status: 'loading' });

  const exchange = useCallback(async () => {
    const res = await authAPI.tenantToken(slug);
    setTenantToken(res.data.access_token);
    // The features its plan includes (Feature Access Control). If they can't be read, nothing is hidden here; the API
    // still refuses what the plan doesn't include.
    // tier: which dashboard its plan uses (plans the admin adds follow a tier). If this can't be read, the plan code is
    // used as the tier (right for the base plans).
    const extra = await tenantAPI.get().then((r) => r.data).catch(() => null);
    setState({ status: 'ready', tenant: res.data.tenant, role: res.data.role, permissions: res.data.permissions,
      features: extra?.features || null, tier: extra?.tier || res.data.tenant.plan, onboarding: extra?.onboarding || 'approved',
      tour: extra?.tour || null });
    return res.data;
  }, [slug]);

  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      // Logged out: a plain login page. Session ran out (or none yet): sign in, then back to this page.
      if (signedOut) goTo('/login');
      else toLogin();
      return;
    }
    setTenantTokenRefresher(async () => {
      try {
        await exchange();
      } catch (err) {
        if (err.response?.status === 401) toLogin();
        else if (leaveIfNotApproved(err)) setState({ status: 'loading' });
        else setState({ status: 'forbidden', message: apiError(err, 'Your access to this organization changed') });
        throw err;
      }
    });
    exchange().catch((err) => {
      setTenantToken(null);
      if (leaveIfNotApproved(err)) return;
      setState({
        status: err.response?.status === 403 ? 'forbidden' : 'error',
        message: apiError(err, 'Could not open this organization'),
      });
    });
    return () => setTenantTokenRefresher(null);
  }, [authLoading, userId, signedOut, exchange]);

  const reloadTenant = useCallback(async () => {
    const res = await tenantAPI.get();
    setState((s) => ({ ...s, tenant: res.data.tenant, role: res.data.role, permissions: res.data.permissions,
      features: res.data.features || s.features, tier: res.data.tier || s.tier, onboarding: res.data.onboarding || s.onboarding,
      tour: res.data.tour || s.tour }));
  }, []);

  const can = useCallback((perm) => (state.permissions || []).includes(perm), [state.permissions]);
  /** Whether the company's plan includes a feature (keys in lib/features.js). No key = nothing needed. */
  const hasFeature = useCallback((key) => !key || !state.features || state.features.includes(key), [state.features]);

  return (
    <TenantContext.Provider value={{ slug, ...state, can, hasFeature, reloadTenant }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) throw new Error('useTenant must be used within TenantProvider');
  return context;
};
